import React, { useState, useEffect } from 'react';
import { getAllDiagnosisReports } from '../../services/diagnosisReports.service';
import { getPlanningStats } from '../../services/planning.service';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import '../../styles/admin-reports.css';

const DetailedReports = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('all'); // all, day, week, month, custom
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [filteredReports, setFilteredReports] = useState([]);
    const [planningStats, setPlanningStats] = useState({ roadmapRevenue: 0, handsonRevenue: 0, coachingRevenue: 0 });
    const [stats, setStats] = useState({ total: 0, totalRevenue: 0, avgRevenue: 0 });
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        fetchReports();
    }, []);

    useEffect(() => {
        filterReports();
        setCurrentPage(1); // Reset to first page on filter/period change
    }, [reports, period, startDate, endDate]);

    const fetchReports = async () => {
        try {
            setLoading(true);
            const [data, pStats] = await Promise.all([
                getAllDiagnosisReports(),
                getPlanningStats()
            ]);
            setReports(data);
            setPlanningStats(pStats);
        } catch (error) {
            console.error('Error fetching reports:', error);
        } finally {
            setLoading(false);
        }
    };

    const getDetailedIncomeType = (report) => {
        const info = report.personalInfo || {};
        const biz = report.businessDetails || {};
        const incomeType = info.incomeType || '';
        const empStatus = (info.employmentStatus || '').toLowerCase();

        // 1. Handle Salary
        if (empStatus === 'salaried' || incomeType === 'salary') {
            return 'Salary';
        }

        // 2. Handle Business - Partnership
        if (incomeType === 'business_partnership') {
            return 'Business (Partnership)';
        }

        // 3. Handle Business - Proprietorship with Nature
        if (incomeType === 'business_proprietor' || empStatus === 'self-employed') {
            const nature = biz.natureOfBusiness || biz.businessCategory || '';
            return `Business (Proprietor)${nature ? ` - ${nature}` : ''}`;
        }

        // 4. Default to raw income/other type if available, else N/A
        return info.otherIncomeType || info.incomeType || 'N/A';
    };

    const calculateReportIncome = (report) => {
        const sal = report.salary || {};
        const personal = report.personalInfo || {};

        // 1. Try totalIncome field first
        if (sal.totalIncome) return parseFloat(sal.totalIncome);

        // 2. Partnership profit if applicable
        if (report.businessAnalysis?.taxableIncome) return parseFloat(report.businessAnalysis.taxableIncome);

        // 3. Manual sum (Salary + Other Income)
        const base = parseFloat(sal.monthlySalary || sal.baseSalary || personal.monthlySalary) || 0;
        const other = parseFloat(personal.otherIncomeAmount) || 0;
        return base + other;
    };

    const filterReports = () => {
        const now = new Date();

        // Time constant for Last 30 Days
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(now.getDate() - 30);
        thirtyDaysAgo.setHours(0, 0, 0, 0);

        // Boundaries for filtering
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        startOfDay.setHours(0, 0, 0, 0); // Ensure start of day

        const currentDay = now.getDay(); // 0 is Sunday
        const diffToMonday = now.getDate() - currentDay + (currentDay === 0 ? -6 : 1);
        const startOfWeek = new Date(now.getFullYear(), now.getMonth(), diffToMonday);
        startOfWeek.setHours(0, 0, 0, 0);

        let filtered = reports.filter(report => {
            if (!report.createdAt) return false;
            const reportDate = new Date(report.createdAt);

            if (period === 'day') return reportDate >= startOfDay;
            if (period === 'week') return reportDate >= startOfWeek;
            if (period === 'month') return reportDate >= thirtyDaysAgo;
            if (period === 'custom') {
                const start = startDate ? new Date(startDate) : new Date(0);
                const end = endDate ? new Date(endDate) : new Date();
                end.setHours(23, 59, 59, 999);
                return reportDate >= start && reportDate <= end;
            }
            return true;
        });

        const completedReports = filtered.filter(r => r.status !== 'incomplete');
        const diagnosisRevenue = completedReports.reduce((sum, r) => {
            const isDemo = r.payment?.paymentId?.startsWith('DEMO_');
            if (isDemo) return sum;
            return sum + (r.payment?.amount || 299);
        }, 0);

        const totalRevenue = diagnosisRevenue +
            (planningStats.roadmapRevenue || 0) +
            (planningStats.handsonRevenue || 0) +
            (planningStats.coachingRevenue || 0);

        const avgRevenue = completedReports.length > 0 ? (totalRevenue / completedReports.length) : 0;

        setStats({
            total: filtered.length,
            totalRevenue: totalRevenue,
            avgRevenue: avgRevenue
        });

        setFilteredReports(filtered);
    };

    // Pagination calculations
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredReports.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredReports.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const downloadExcel = () => {
        if (filteredReports.length === 0) return;

        const data = filteredReports.map(r => {
            const isIncomplete = r.status === 'incomplete';
            return {
                "Timestamp": new Date(r.createdAt).toLocaleString(),
                "Report ID": r.reportId || 'N/A',
                "Participant Name": r.personalInfo?.name || 'N/A',
                "Email Address": r.personalInfo?.email || 'N/A',
                "Contact Number": r.personalInfo?.phone || 'N/A',
                "City": r.personalInfo?.city || 'N/A',
                "Income Type": getDetailedIncomeType(r),
                "Paid Amount": isIncomplete ? "Incomplete Form" : (r.payment?.amount || 299),
                "Business Name": r.businessDetails?.businessName || 'N/A',
                "Business Type": r.businessDetails?.businessType || 'N/A',
                "Status": r.status || 'Success'
            };
        });

        // Create a new workbook and worksheet
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Financial Reports");

        // Format worksheet columns width
        const wscols = [
            { wch: 20 }, // Timestamp
            { wch: 15 }, // Report ID
            { wch: 20 }, // Name
            { wch: 25 }, // Email
            { wch: 15 }, // Phone
            { wch: 15 }, // City
            { wch: 25 }, // Type
            { wch: 15 }, // Paid Amount
            { wch: 25 }, // Biz Name
            { wch: 20 }, // Biz Type
            { wch: 10 }  // Status
        ];
        worksheet['!cols'] = wscols;

        // Save file
        XLSX.writeFile(workbook, `Yathartha_Reports_${period}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(value || 0);
    };

    if (loading) {
        return (
            <div className="admin-content-v2" style={{ display: 'flex', minHeight: '80vh' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', flex: 1 }}>
                    <div className="custom-loader" style={{ width: '55px', height: '80px' }}>
                        <svg viewBox="0 0 55 80" xmlns="http://www.w3.org/2000/svg" fill="#0EAB5EFF">
                            <g transform="matrix(1 0 0 -1 0 80)">
                                <rect width="10" height="20" rx="3">
                                    <animate attributeName="height" begin="0s" dur="4.3s" values="20;45;57;80;64;32;66;45;64;23;66;13;64;56;34;34;2;23;76;79;20" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                                <rect x="15" width="10" height="80" rx="3">
                                    <animate attributeName="height" begin="0s" dur="2s" values="80;55;33;5;75;23;73;33;12;14;60;80" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                                <rect x="30" width="10" height="50" rx="3">
                                    <animate attributeName="height" begin="0s" dur="1.4s" values="50;34;78;23;56;23;34;76;80;54;21;50" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                                <rect x="45" width="10" height="30" rx="3">
                                    <animate attributeName="height" begin="0s" dur="2s" values="30;45;13;80;56;72;45;76;34;23;67;30" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                            </g>
                        </svg>
                    </div>
                    <p style={{ marginTop: '20px', color: '#64748B', fontWeight: 500 }}>Generating detailed financial report...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-content-v2">
            <div className="diagnosis-header-v2">
                <h1 className="nav-title-v2">Detailed Financial Reports</h1>
                <p className="nav-subtitle-v2">Deep dive into participant profiles and income classifications</p>
            </div>

            <div className="report-period-selector">
                <button
                    className={`period-btn ${period === 'all' ? 'active' : ''}`}
                    onClick={() => setPeriod('all')}
                >
                    All Time
                </button>
                <button
                    className={`period-btn ${period === 'day' ? 'active' : ''}`}
                    onClick={() => setPeriod('day')}
                >
                    Today
                </button>
                <button
                    className={`period-btn ${period === 'week' ? 'active' : ''}`}
                    onClick={() => setPeriod('week')}
                >
                    This Week
                </button>
                <button
                    className={`period-btn ${period === 'month' ? 'active' : ''}`}
                    onClick={() => setPeriod('month')}
                >
                    Last 30 Days
                </button>
                <button
                    className={`period-btn ${period === 'custom' ? 'active' : ''}`}
                    onClick={() => setPeriod('custom')}
                >
                    Date Search
                </button>

                {period === 'custom' && (
                    <div className="custom-date-search" style={{ display: 'flex', gap: '8px', alignItems: 'center', marginLeft: '12px' }}>
                        <input
                            type="date"
                            className="date-input"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            placeholder="Start Date"
                            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #E2E8F0', outline: 'none' }}
                        />
                        <span style={{ color: '#64748B' }}>to</span>
                        <input
                            type="date"
                            className="date-input"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            placeholder="End Date"
                            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #E2E8F0', outline: 'none' }}
                        />
                    </div>
                )}
            </div>

            <div className="stats-summary-v2">
                <div className="stat-card-v2 report-stat">
                    <span className="stat-label-v2">Total Participants</span>
                    <span className="stat-value-v2">{stats.total}</span>
                    <span className="stat-sub-v2">{period === 'month' ? 'Last 30 days' : `Entries in ${period}`}</span>
                </div>
                <div className="stat-card-v2 report-stat">
                    <span className="stat-label-v2">Aggregated Volume</span>
                    <span className="stat-value-v2">{formatCurrency(stats.totalRevenue)}</span>
                    <span className="stat-sub-v2">Total Business Revenue</span>
                </div>
                <div className="stat-card-v2 report-stat">
                    <span className="stat-label-v2">Avg. Revenue / User</span>
                    <span className="stat-value-v2">{formatCurrency(stats.avgRevenue)}</span>
                    <span className="stat-sub-v2">Service revenue average</span>
                </div>
            </div>

            <div className="table-card-v2">
                <div className="table-card-header-v2">
                    <h3>Detailed Data Matrix ({period.toUpperCase()})</h3>
                    <button className="download-btn-v2" onClick={downloadExcel}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                        DOWNLOAD DETAILED EXCEL (.XLSX)
                    </button>
                </div>

                <div className="table-responsive">
                    <table className="v2-table">
                        <thead>
                            <tr>
                                <th>Timestamp</th>
                                <th>Participant</th>
                                <th>Income Classification</th>
                                <th>Paid Amount</th>
                                <th style={{ textAlign: 'center' }}>Full Analysis</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map((report) => {
                                const isIncomplete = report.status === 'incomplete';
                                const payAmt = report.payment?.amount || 299;
                                return (
                                    <tr key={report.id}>
                                        <td style={{ fontSize: '11px', color: '#64748B' }}>
                                            {new Date(report.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontWeight: 600, fontSize: '13px' }}>{report.personalInfo?.name || 'N/A'}</span>
                                                <span style={{ fontSize: '11px', color: '#64748B' }}>{report.personalInfo?.email}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span style={{
                                                fontSize: '11px',
                                                background: '#F8FAFC',
                                                padding: '4px 8px',
                                                borderRadius: '6px',
                                                border: '1px solid #E2E8F0',
                                                color: '#334155'
                                            }}>
                                                {getDetailedIncomeType(report)}
                                            </span>
                                        </td>
                                        <td>
                                            {isIncomplete ? (
                                                <span style={{ fontWeight: 500, color: '#f97316' }}>incomplete form</span>
                                            ) : (
                                                <span style={{ fontWeight: 600, color: '#059669' }}>
                                                    {formatCurrency(payAmt)}
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <Link
                                                to={`/admin/diagnosis/reports/${report.reportId}`}
                                                className="icon-btn-v2 view"
                                                style={{ margin: '0 auto' }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                            </Link>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredReports.length === 0 && (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center', padding: '60px', color: '#64748B' }}>
                                        No entries found for the selected period.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                {filteredReports.length > 0 && (
                    <div className="pagination-v2">
                        <div className="pagination-info-v2">
                            Showing <strong>{indexOfFirstItem + 1}</strong> to <strong>{Math.min(indexOfLastItem, filteredReports.length)}</strong> of <strong>{filteredReports.length}</strong> results
                        </div>
                        <div className="pagination-controls-v2">
                            <button
                                className="page-btn-v2"
                                onClick={() => paginate(currentPage - 1)}
                                disabled={currentPage === 1}
                                title="Previous Page"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                            </button>

                            <div className="page-numbers-v2" style={{ display: 'flex', gap: '4px' }}>
                                {[...Array(totalPages)].map((_, i) => (
                                    <button
                                        key={i}
                                        className={`page-btn-v2 ${currentPage === i + 1 ? 'active' : ''}`}
                                        onClick={() => paginate(i + 1)}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                            </div>

                            <button
                                className="page-btn-v2"
                                onClick={() => paginate(currentPage + 1)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                title="Next Page"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DetailedReports;
